/** AWS SigV4 が拒否する目安（±5分）。 */
export const CLOCK_SKEW_THRESHOLD_MS = 5 * 60 * 1000;
export const CLOCK_SKEW_ERROR_NAME = "ClockSkewError";

type ErrorLike = {
  name?: string;
  isClockSkew?: boolean;
  message?: string;
  code?: string | number;
  status?: number;
  response?: { status?: number };
  errors?: Array<{ message?: string; errorType?: string }>;
};

function asError(error: unknown): ErrorLike | null {
  if (!error || typeof error !== "object") return null;
  return error as ErrorLike;
}

function errorText(error: ErrorLike): string {
  return [error.message, error.code, error.errors?.[0]?.message, error.errors?.[0]?.errorType]
    .filter(Boolean)
    .join(" ");
}

export function isNetworkError(error: unknown): boolean {
  const e = asError(error);
  if (!e) return false;
  return e.errors?.[0]?.message === "Network Error" || e.message === "Network Error";
}

export function getHttpStatus(error: unknown): number | undefined {
  const e = asError(error);
  if (!e) return undefined;
  return e.response?.status ?? e.status;
}

export function isForbiddenError(error: unknown): boolean {
  const status = getHttpStatus(error);
  return status === 401 || status === 403;
}

/** レスポンス本文や SDK が時刻ずれと分かるとき。 */
export function isClockSkewError(error: unknown): boolean {
  const e = asError(error);
  if (!e) return false;
  if (e.isClockSkew || e.name === CLOCK_SKEW_ERROR_NAME) return true;
  return /RequestTimeTooSkewed|difference between the request time and the current time is too large|Signature expired/i.test(
    errorText(e)
  );
}

export function markClockSkew(error: unknown): unknown {
  const e = asError(error);
  if (e) {
    e.name = CLOCK_SKEW_ERROR_NAME;
    e.isClockSkew = true;
  }
  return error;
}

type FetchLike = (input: string) => Promise<{ headers: { get(name: string): string | null } }>;

function defaultFetch(input: string): Promise<{ headers: { get(name: string): string | null } }> {
  return fetch(input, { method: "HEAD", cache: "no-store" });
}

let cachedDetection: Promise<boolean> | null = null;

function parseDateHeaderMs(value: string | null): number | null {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

async function readServerNowMs(fetchImpl: FetchLike): Promise<number | null> {
  try {
    const response = await fetchImpl("/");
    return parseDateHeaderMs(response.headers.get("date"));
  } catch {
    return null;
  }
}

/**
 * 同一オリジンの Date ヘッダ（CloudFront なら AWS 時刻）と端末時刻を比べる。
 * 取れないときは false（推測しない）。
 */
export async function detectDeviceClockSkew(options?: {
  fetchImpl?: FetchLike;
  deviceNowMs?: number;
  thresholdMs?: number;
}): Promise<boolean> {
  const useCache = !options;
  if (useCache && cachedDetection) return cachedDetection;

  const run = async () => {
    const fetchImpl: FetchLike | null = options?.fetchImpl ?? clockSkewDetection.fetch ?? null;
    if (!fetchImpl) return false;
    const serverNowMs = await readServerNowMs(fetchImpl);
    if (serverNowMs == null) return false;
    const deviceNowMs = options?.deviceNowMs ?? Date.now();
    const thresholdMs = options?.thresholdMs ?? CLOCK_SKEW_THRESHOLD_MS;
    return Math.abs(deviceNowMs - serverNowMs) > thresholdMs;
  };

  const detection = run().then((skewed) => {
    if (useCache && !skewed) cachedDetection = null;
    return skewed;
  });
  if (useCache) cachedDetection = detection;
  return detection;
}

/** テストでキャッシュと検出を差し替える。 */
export const clockSkewDetection = {
  fetch: typeof fetch === "function" ? defaultFetch : (null as FetchLike | null),
  detect: detectDeviceClockSkew,
  reset() {
    cachedDetection = null;
    clockSkewDetection.fetch = typeof fetch === "function" ? defaultFetch : null;
  },
};
